import { useState } from 'react';
import { Box, Text, Flex, Button, Skeleton } from '@chakra-ui/react';
import { Check, XCircle, Clock, Tag } from 'lucide-react';
import type { HourRequest } from './useVolunteersPage';
import { ModalShell } from './components';
import { DurationStepper } from '@/components/ui/DurationStepper';
import { hoursToMinutes, validateDuration } from '@/utils/volunteerHours';

interface Props {
  open: boolean;
  onClose: () => void;
  requests: HourRequest[];
  loading: boolean;
  onAccept: (request: HourRequest, overrideMinutes?: number) => void;
  onDeny: (requestId: string) => void;
}

export function VolunteersHourRequestsModal({ open, onClose, requests, loading, onAccept, onDeny }: Props) {
  // Per-request edited time in minutes, keyed by request id; absent = accept as requested.
  const [editedMinutes, setEditedMinutes] = useState<Record<string, number>>({});

  return (
    <ModalShell open={open} onClose={onClose} title="Hour Requests">
      {loading ? (
        <Box display="flex" flexDirection="column" gap="3">
          {[0, 1, 2].map((k) => (
            <Skeleton key={k} h="100px" borderRadius="xl" />
          ))}
        </Box>
      ) : requests.length === 0 ? (
        <Box textAlign="center" py="10" display="flex" flexDirection="column" alignItems="center" gap="3">
          <Clock size={32} style={{ color: 'var(--chakra-colors-volyx-textSecondary)', opacity: 0.7 }} />
          <Text fontSize="sm" color="volyx.textSecondary">No hour requests for this event</Text>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap="3">
          {requests.map((req) => {
            const name = req.profiles
              ? `${req.profiles.first_name || ''} ${req.profiles.last_name || ''}`.trim()
              : 'Volunteer';
            const initials = name.split(' ').slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
            const profileImage = req.profiles?.profile_image;
            const requestedMinutes = hoursToMinutes(req.hours);
            const edited = editedMinutes[req.id];
            const minutes = edited ?? requestedMinutes;
            const minutesError = validateDuration(minutes);

            return (
              <Box
                key={req.id}
                bg="volyx.lightGray"
                borderRadius="xl"
                p="4"
                border="1px solid"
                borderColor="volyx.borderLight"
                boxShadow="0 1px 3px rgba(0,0,0,0.06)"
              >
                {/* Volunteer info row */}
                <Flex alignItems="center" gap="3" mb="2">
                  <Box
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg="volyx.primaryLight"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                    overflow="hidden"
                  >
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Text fontSize="xs" fontWeight="bold" color="volyx.primary">{initials}</Text>
                    )}
                  </Box>
                  <Box flex="1" minW="0">
                    <Text fontSize="sm" fontWeight="semibold" color="volyx.primary" truncate m="0">{name}</Text>
                    {req.description && (
                      <Text fontSize="xs" color="volyx.textSecondary" m="0" truncate>
                        {req.description}
                      </Text>
                    )}
                  </Box>
                </Flex>

                {/* Task badge (if linked to a task) */}
                {req.task_title && (
                  <Flex
                    align="center"
                    gap="1.5"
                    mb="2"
                    px="2.5"
                    py="1"
                    bg="blue.50"
                    borderRadius="full"
                    display="inline-flex"
                    border="1px solid"
                    borderColor="blue.100"
                  >
                    <Tag size={11} style={{ color: 'var(--chakra-colors-blue-500)', flexShrink: 0 }} />
                    <Text fontSize="xs" fontWeight="semibold" color="blue.600" m="0" truncate>
                      {req.task_title}
                    </Text>
                  </Flex>
                )}

                {/* Time range */}
                <Text fontSize="xs" color="volyx.textSecondary" mb="3" mt="1">
                  {new Date(req.start_time).toLocaleDateString()} · {new Date(req.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – {new Date(req.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Text>

                {/* Editable time */}
                <Flex alignItems="center" gap="3" mb="3" flexWrap="wrap">
                  <Text fontSize="xs" color="volyx.textSecondary" flexShrink={0} m="0">
                    Time to accept:
                  </Text>
                  <DurationStepper
                    size="sm"
                    value={minutes}
                    onChange={(m) => setEditedMinutes((prev) => ({ ...prev, [req.id]: m }))}
                  />
                  {edited !== undefined && edited !== requestedMinutes && (
                    <Text fontSize="10px" color="orange.500" m="0" flexShrink={0}>
                      modified
                    </Text>
                  )}
                  {minutesError && (
                    <Text fontSize="xs" color="volyx.error" m="0" mb="2">
                      {minutesError}
                    </Text>
                  )}
                </Flex>

                {/* Actions */}
                <Flex gap="2">
                  <Button
                    size="sm"
                    bg="volyx.success"
                    color="white"
                    borderRadius="lg"
                    border="none"
                    cursor="pointer"
                    disabled={minutesError !== null}
                    _hover={{ opacity: 0.9 }}
                    onClick={() => onAccept(req, edited)}
                  >
                    <Flex as="span" alignItems="center" gap="1.5">
                      <Check size={14} />
                      Accept
                    </Flex>
                  </Button>
                  <Button
                    size="sm"
                    bg="transparent"
                    border="1px solid"
                    borderColor="volyx.error"
                    color="volyx.error"
                    borderRadius="lg"
                    cursor="pointer"
                    _hover={{ bg: 'volyx.lightGray' }}
                    onClick={() => onDeny(req.id)}
                  >
                    <Flex as="span" alignItems="center" gap="1.5">
                      <XCircle size={14} />
                      Deny
                    </Flex>
                  </Button>
                </Flex>
              </Box>
            );
          })}
        </Box>
      )}
    </ModalShell>
  );
}
